import {
  Ref, nextTick, ref,
} from 'vue';
import {
  L1Validations,
  HekateDisplayField,
  HekateDisplayForm,
  Trans,
  Translations,
  FormIdUtil,
  FieldHtmlNameUtil,
} from 'www-shared';
import bus from '../../../../../bus';

import ErrorUtil from '../../../../util/ErrorUtil';
import ExternalUtil from '../../../../ExternalUtil';
import UnifiedSignupSessionInfo from './session-info/UnifiedSignupSessionInfo';
import UnifiedSignupScreenProvider from './UnifiedSignupScreenProvider';
import UnifiedSignupScreenComponent from './UnifiedSignupScreenComponent';
import UnifiedSignupTemplateGetter from './UnifiedSignupTemplateGetter';
import UnifiedSignupStartScreen from './session-info/UnifiedSignupStartScreen';
import FormSubmitter from '../FormSubmitter';
import OnSubmitFormFunction from '../../../../../model/OnSubmitFormFunction';
import { version } from '../../../../../../package.json';

export default class UnifiedSignupFormFacetCoordinator {
  private readonly formDefinition: Ref<HekateDisplayForm>;

  private readonly formElement: Ref<null | HTMLFormElement>;

  private readonly isShowThankYou: Ref<boolean>;

  private readonly isFormSubmitting: Ref<boolean>;

  private readonly supportEmailText: Ref<string>;

  private readonly successMessageHtml: Ref<string>;

  private readonly isShowSuccessMessage: Ref<boolean>;

  private readonly isShow409Error: Ref<boolean>;

  private readonly onSubmitForm: OnSubmitFormFunction;

  private readonly buildSupportEmailErrorText: VoidFunction;

  private readonly sessionInfo: Ref<UnifiedSignupSessionInfo>;

  private readonly screenProvider: UnifiedSignupScreenProvider;

  private readonly screenComponents: Ref<UnifiedSignupScreenComponent[]>;

  private readonly validateComponents: Ref<UnifiedSignupScreenComponent[]>;

  private readonly screenLabel: Ref<string>;

  private readonly isFormLoading: Ref<boolean>;

  private readonly isNextDisabled: Ref<boolean>;

  private readonly isNextButtonHidden: Ref<boolean>;

  private readonly isBackButtonHidden: Ref<boolean>;

  private readonly nextButtonLabel: Ref<string>;

  private numFailedFormSubmit = 0;

  private readonly maxNumFailedFormSubmit = 3;

  private submitText: string;

  constructor({
    formDefinition,
    formElement,
    supportEmailText,
    isFormSubmitting,
    isShowThankYou,
    onSubmitForm,
    isShowSuccessMessage,
    successMessageHtml,
    isShow409Error,
    startScreen,
    submitText,
    buildSupportEmailErrorText,
  }: {
    formDefinition: Ref<HekateDisplayForm>,
    formElement: Ref<null | HTMLFormElement>;
    supportEmailText: Ref<string>,
    isFormSubmitting: Ref<boolean>,
    isShowThankYou: Ref<boolean>,
    onSubmitForm: OnSubmitFormFunction
    isShowSuccessMessage: Ref<boolean>,
    successMessageHtml: Ref<string>,
    isShow409Error: Ref<boolean>,
    startScreen: string,
    submitText: string,
    buildSupportEmailErrorText: VoidFunction,
  }) {
    this.onSubmitForm = onSubmitForm;
    this.formDefinition = formDefinition;
    this.formDefinition.value.isRecaptcha = true;
    this.formElement = formElement;
    this.isShowThankYou = isShowThankYou;
    this.isFormSubmitting = isFormSubmitting;
    this.supportEmailText = supportEmailText;
    this.successMessageHtml = successMessageHtml;
    this.isShowSuccessMessage = isShowSuccessMessage;
    this.isShow409Error = isShow409Error;
    this.isFormLoading = ref(false);
    this.isNextButtonHidden = ref(false);
    this.isBackButtonHidden = ref(true);
    this.nextButtonLabel = ref('');
    this.submitText = submitText;

    const sessionInfo = new UnifiedSignupSessionInfo({
      startScreen: UnifiedSignupStartScreen.fromString(startScreen),
    });
    this.sessionInfo = ref(sessionInfo);

    const signupSurveyDiv = document.querySelector<HTMLDivElement>('#signup-survey');
    this.screenProvider = new UnifiedSignupScreenProvider({
      sessionInfo: this.sessionInfo,
      formDefinition: this.formDefinition,
      onUpdatePage: this.onUpdatePage.bind(this),
      onSubmitForm: this.onSubmitForm,
      isNextButtonHidden: this.isNextButtonHidden,
      isBackButtonHidden: this.isBackButtonHidden,
      isFormSubmitting: this.isFormSubmitting,
      signupSurveyDiv,
      submitText: this.submitText,
    });
    this.screenComponents = ref([]);
    this.validateComponents = ref([]);
    this.screenLabel = ref('loading....');
    this.isNextDisabled = ref(true);

    this.monitorValidations();
    this.buildSupportEmailErrorText = buildSupportEmailErrorText;

    // eslint-disable-next-line no-console
    console.log('build version', version);
  }

  private monitorValidations() {
    setInterval(() => {
      const displayFields = this.validateComponents.value.map((cmp) => cmp.displayField);
      const validationResults = L1Validations.checkValidation({ fields: displayFields });

      this.validateComponents.value.forEach((cmp, ii) => {
        if (!cmp.displayField.val) return;
        cmp.displayField.errorMessages.splice(0, cmp.displayField.errorMessages.length);
        const found = validationResults.getFieldErrors().find((err) => err.index === ii);
        if (!found) return;
        if (cmp.displayField.errorMessages.length > 0) return;

        cmp.displayField.errorMessages.push({ message: found.message });
      });

      const nonErrors = this.validateComponents.value
        .filter((sc) => !!sc.displayField.val && sc.displayField.errorMessages.length === 0);

      nonErrors.forEach((cmp) => {
        cmp.displayField.isValidated = true;
      });

      this.isNextDisabled.value = validationResults.getFieldErrors().length > 0;
    }, 150);
  }

  /** Validate the field on blur */
  private static onFieldBlur(field: HekateDisplayField) {
    L1Validations.blurSingleField({ field }).then((fieldError) => {
      field.errorMessages.splice(0, field.errorMessages.length);
      field.isValidated = false;
      if (fieldError === null) {
        field.isValidated = field.val !== '';
        return;
      }
      field.errorMessages.push({
        message: fieldError.message,
        hint: fieldError.hint,
      });
    });
  }

  /** Clear/Reset validation on focus */
  private static onFieldFocus(field: HekateDisplayField) {
    field.isValidated = false;
  }

  private clear409Error() {
    this.isShow409Error.value = false;
    ExternalUtil.safeResetRecaptcha();
  }

  public async onUpdatePage() {
    // eslint-disable-next-line no-console
    console.log('sessionInfo', JSON.parse(JSON.stringify(this.sessionInfo.value)));

    ExternalUtil.scrollToTopOfForm({ formId: FormIdUtil.names.unifiedSignup });
    FormSubmitter.handleFormErrorMessages({ formDefinition: this.formDefinition.value });
    const isInvalid = this.validateComponents.value
      .find((sc) => L1Validations.validateSingleField({ field: sc.displayField, index: 0 }));
    if (!this.sessionInfo.value.isChooseOwnPlan && (this.isNextDisabled.value || isInvalid)) {
      // eslint-disable-next-line no-console
      console.warn('Form is disabled');
      return;
    }
    const contentWrapper = document.getElementById('uss-form-container') as HTMLDivElement;
    const hekateLoader = document.getElementById('hekate-loader') as HTMLDivElement;
    hekateLoader.classList.remove('d-none');
    contentWrapper.classList.add('hekate-screen-loading');

    this.isFormLoading.value = true;
    document.querySelector('body')?.classList.add('hekate-uss');

    this.formDefinition.value.screenNumber += 1;

    // copy the prev screen (will be prev screen after running `toNextScreen`)
    const prevScreen = this.screenProvider.getScreen();

    // advanced the screen
    const isSuccess = await this.screenProvider.getScreen().toNextScreen();

    this.handleFormSubmitErrors();

    if (!isSuccess) {
      // eslint-disable-next-line no-console
      console.warn('NOT SUCCESS');
      hekateLoader.classList.add('d-none');
      contentWrapper.classList.remove('hekate-screen-loading');
      this.isFormLoading.value = false;
      return;
    }

    // stop watching for changes on the previous screen
    prevScreen.stopWatchers();

    // display the screen
    await this.displayScreen();

    this.isFormLoading.value = false;
  }

  private handleFormSubmitErrors() {
    if (this.sessionInfo.value.formSubmitStatus !== 'submit-failed') {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('Failure count:', this.numFailedFormSubmit);
    this.numFailedFormSubmit += 1;
    if (this.numFailedFormSubmit >= this.maxNumFailedFormSubmit) {
      this.buildSupportEmailErrorText();
      this.sessionInfo.value.isErroredOut = true;
      return;
    }

    if (this.formDefinition.value.errorMessages.length === 0) {
      this.formDefinition.value.errorMessages.push({
        message: Trans.byKey('formGenericFieldErrorMessage'),
      });
    }
  }

  private async displayScreen() {
    this.isFormLoading.value = true;

    const contentWrapper = document.getElementById('uss-form-container') as HTMLDivElement;
    const hekateLoader = document.getElementById('hekate-loader') as HTMLDivElement;
    // get screen that should be shown
    const curScreen = this.screenProvider.getScreen();
    // clear validation for current components.
    curScreen.getVisibleComponents().forEach((cmp) => {
      cmp.displayField.isValidated = false;
    });
    this.screenComponents.value = curScreen.getVisibleComponents();
    this.validateComponents.value = curScreen.getValidatedComponents()
      .filter((cmp) => cmp.displayField.htmlName !== FieldHtmlNameUtil.recaptcha)
      .map((cmp) => {
        cmp.displayField.isRequired = true;
        return cmp;
      });
    this.screenLabel.value = curScreen.screenName;
    this.nextButtonLabel.value = curScreen.getNextButtonLabel();

    // cache the current app container while we replace the contents of the
    // screen (can be empty, in which case we can never get the form back)
    const appBox = document.querySelector('div[form-id="182"]');

    const partialHtml = (curScreen.isInitialScreen())
      ? contentWrapper
      : await UnifiedSignupTemplateGetter.getHtml({ partialName: curScreen.screenName });

    if (!curScreen.isInitialScreen()) {
      contentWrapper.innerHTML = '';
      contentWrapper.append(partialHtml);
    }

    const formPlaceholder = document.querySelector('.hekate-placeholder');
    if (formPlaceholder) {
      if (!appBox) {
        throw ErrorUtil.internalError({ msg: 'could not find appBox for unified signup form' });
      }
      formPlaceholder.appendChild(appBox);
    }

    nextTick(() => {
      curScreen.initWatchers();
      curScreen.initListeners({ partialDiv: partialHtml });
      curScreen.trackEvent();
      curScreen.updateProgress();
      if (contentWrapper) {
        contentWrapper.classList.remove('hekate-screen-loading');
      }
      if (hekateLoader) {
        hekateLoader.classList.add('d-none');
      }
      bus.dispatchEvent('uss-screen-shown', { screenName: curScreen.screenName });
    });

    this.isFormLoading.value = false;
  }

  public async onBack() {
    const curScreen = this.screenProvider.getScreen();
    curScreen.toPreviousScreen();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setup() {
    Trans.populateTranslations({
      language: ExternalUtil.getSiteLang(),
      translations: Translations,
    });

    if (window.wwwHekateTranslations) {
      Trans.populateTranslations({
        language: ExternalUtil.getSiteLang(),
        translations: window.wwwHekateTranslations,
      });
    }
    this.displayScreen();

    return {
      formElement: this.formElement,
      formDefinition: this.formDefinition,
      isShowThankYou: this.isShowThankYou,
      isFormSubmitting: this.isFormSubmitting,
      supportEmailText: this.supportEmailText,
      successMessageHtml: this.successMessageHtml,
      isShowSuccessMessage: this.isShowSuccessMessage,
      isShow409Error: this.isShow409Error,
      onFieldBlur: UnifiedSignupFormFacetCoordinator.onFieldBlur.bind(this),
      onFieldFocus: UnifiedSignupFormFacetCoordinator.onFieldFocus,
      onSubmitForm: this.onSubmitForm,
      clear409Error: this.clear409Error.bind(this),
      onUpdatePage: this.onUpdatePage.bind(this),
      onEnterListener: this.onUpdatePage.bind(this),
      sessionInfo: this.sessionInfo,
      screenComponents: this.screenComponents,
      screenLabel: this.screenLabel,
      isFormLoading: this.isFormLoading,
      isNextDisabled: this.isNextDisabled,
      isNextButtonHidden: this.isNextButtonHidden,
      isBackButtonHidden: this.isBackButtonHidden,
      onBack: this.onBack.bind(this),
      nextButtonLabel: this.nextButtonLabel,
    };
  }
}
